import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import Navbar from "@/components/Navbar";
import { ADMIN } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import {
  Users, FileText, Share2, TrendingUp, Search, Trash2,
  CheckCircle, AlertTriangle, AlertCircle, X, Shield, ShieldOff,
  ChevronRight, Calendar, ExternalLink, BarChart2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

function initials(name = "") {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

function StatusBadge({ status }) {
  const map = {
    normal:             { cls: "badge-normal",    label: "Normal" },
    requires_attention: { cls: "badge-requires",  label: "Attention" },
    critical:           { cls: "badge-abnormal",  label: "Critical" },
  };
  const { cls, label } = map[status] || { cls: "badge-normal", label: status };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{label}</span>;
}

function StatCard({ icon: Icon, value, label, color, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color}`}>
          <Icon size={17} className="text-white" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</span>
      </div>
      <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function ConfirmDeleteModal({ target, onConfirm, onCancel, loading }) {
  if (!target) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onCancel} />
      <div data-testid={ADMIN.confirmDeleteModal}
        className="relative bg-white border border-slate-200 rounded-xl shadow-xl w-full max-w-sm p-6">
        <button onClick={onCancel}
          className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors">
          <X size={16} />
        </button>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle size={20} className="text-red-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-slate-900">Delete {target.type}</h3>
            <p className="text-xs text-slate-400">This cannot be undone</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-2">Are you sure you want to delete:</p>
        <p className="text-sm font-semibold text-slate-900 mb-5 truncate">{target.label}</p>
        {target.type === "user" && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-4">
            All reports for this user will also be permanently deleted.
          </p>
        )}
        <div className="flex gap-3">
          <button data-testid={ADMIN.cancelDeleteBtn} onClick={onCancel} disabled={loading}
            className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button data-testid={ADMIN.confirmDeleteBtn} onClick={onConfirm} disabled={loading}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Deleting…</> : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({ stats, reports }) {
  const recent = (reports || []).slice(0, 8);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard icon={Users}     value={stats.total_users}       label="Total Users"         color="bg-blue-900" />
        <StatCard icon={FileText}  value={stats.total_reports}     label="Total Reports"       color="bg-emerald-600" />
        <StatCard icon={TrendingUp} value={stats.reports_this_week} label="Reports This Week"  color="bg-amber-500" />
        <StatCard icon={Share2}    value={stats.shared_reports}    label="Shared Reports"      color="bg-violet-600" />
      </div>

      {/* Status breakdown */}
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
          Reports by Status
        </h3>
        <div className="space-y-3">
          {[
            { key: "normal",            label: "Normal",          color: "bg-emerald-500", icon: CheckCircle },
            { key: "requires_attention",label: "Needs Attention", color: "bg-amber-400",   icon: AlertTriangle },
            { key: "critical",          label: "Critical",        color: "bg-red-500",     icon: AlertCircle },
          ].map(({ key, label, color, icon: Icon }) => {
            const count = stats.by_status?.[key] || 0;
            const pct = stats.total_reports ? Math.round((count / stats.total_reports) * 100) : 0;
            return (
              <div key={key} className="flex items-center gap-3">
                <Icon size={14} className={color.replace("bg-","text-")} />
                <span className="text-sm text-slate-600 w-32">{label}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-2">
                  <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm font-semibold text-slate-900 w-8 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent reports */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Recent Reports
          </h3>
        </div>
        <div className="divide-y divide-slate-100">
          {recent.map((r) => (
            <div key={r._id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  r.overall_status === "critical" ? "bg-red-500" :
                  r.overall_status === "requires_attention" ? "bg-amber-400" : "bg-emerald-400"}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{r.patient_info?.name || "Unknown"}</p>
                  <p className="text-xs text-slate-400 truncate">{r.user_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <StatusBadge status={r.overall_status} />
                <span className="text-xs text-slate-400 hidden sm:block">{fmtDate(r.created_at)}</span>
                <Link to={`/reports/${r._id}`} className="text-slate-300 hover:text-blue-900 transition-colors">
                  <ExternalLink size={13} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────

function UsersTab({ users, currentUserId, onDelete, onRoleChange }) {
  const [search, setSearch]         = useState("");
  const [promotingId, setPromotingId] = useState(null); // userId pending confirm
  const [changingId,  setChangingId]  = useState(null); // userId mid-request

  const filtered = useMemo(() =>
    users.filter(u =>
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase())
    ), [users, search]);

  const handleConfirmRole = async (userId, newRole) => {
    setChangingId(userId);
    await onRoleChange(userId, newRole);
    setPromotingId(null);
    setChangingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid={ADMIN.searchInput}
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search users by name or email…"
          className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
        />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {["User", "Email", "Role", "Reports", "Joined", "Actions"].map(h => (
                <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((u) => {
              const isSelf = u._id === currentUserId;
              const isPending = promotingId === u._id;
              const isChanging = changingId === u._id;
              const newRole = u.role === "admin" ? "user" : "admin";
              return (
                <tr key={u._id} data-testid={`${ADMIN.userRow}-${u._id}`}
                  className={`transition-colors ${isPending ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        u.role === "admin" ? "bg-blue-900" : "bg-slate-500"
                      }`}>
                        <span className="text-white text-xs font-bold">{initials(u.name)}</span>
                      </div>
                      <span className="text-sm font-medium text-slate-900">{u.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-slate-500">{u.email}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      u.role === "admin"
                        ? "bg-blue-100 text-blue-800 border border-blue-200"
                        : "bg-slate-100 text-slate-600 border border-slate-200"
                    }`}>
                      {u.role === "admin" ? "Admin" : "User"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm font-semibold text-slate-900">{u.report_count}</td>
                  <td className="py-3 px-4 text-sm text-slate-400">{fmtDate(u.created_at)}</td>
                  <td className="py-3 px-4">
                    {isSelf ? (
                      <span className="text-xs text-slate-300 italic">You</span>
                    ) : isPending ? (
                      /* Inline confirm */
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-600 font-medium whitespace-nowrap">
                          {newRole === "admin" ? "Promote to Admin?" : "Demote to User?"}
                        </span>
                        <button
                          data-testid={`${ADMIN.confirmRoleButton}-${u._id}`}
                          onClick={() => handleConfirmRole(u._id, newRole)}
                          disabled={isChanging}
                          className="text-xs font-semibold text-blue-900 hover:underline disabled:opacity-50">
                          {isChanging ? "…" : "Confirm"}
                        </button>
                        <button
                          data-testid={`${ADMIN.cancelRoleButton}-${u._id}`}
                          onClick={() => setPromotingId(null)}
                          disabled={isChanging}
                          className="text-xs text-slate-400 hover:underline disabled:opacity-50">
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        {/* Role toggle button */}
                        <button
                          data-testid={`${u.role === "admin" ? ADMIN.demoteUserButton : ADMIN.promoteUserButton}-${u._id}`}
                          onClick={() => setPromotingId(u._id)}
                          title={newRole === "admin" ? "Promote to Admin" : "Demote to User"}
                          className={`p-1.5 rounded transition-colors ${
                            u.role === "admin"
                              ? "text-slate-300 hover:text-amber-600 hover:bg-amber-50"
                              : "text-slate-300 hover:text-blue-700 hover:bg-blue-50"
                          }`}>
                          {u.role === "admin" ? <ShieldOff size={14} /> : <Shield size={14} />}
                        </button>
                        {/* Delete button — only for non-admin users */}
                        {u.role !== "admin" && (
                          <button
                            data-testid={`${ADMIN.deleteUserButton}-${u._id}`}
                            onClick={() => onDelete({ type: "user", id: u._id, label: `${u.name} (${u.email})` })}
                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center text-slate-400 text-sm py-8">No users found</p>
        )}
      </div>
    </div>
  );
}

// ── Reports tab ───────────────────────────────────────────────────────────────

function ReportsTab({ reports, onDelete }) {
  const [search,       setSearch]       = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const filtered = useMemo(() =>
    reports.filter(r => {
      const name  = (r.patient_info?.name || "").toLowerCase();
      const email = (r.user_email || "").toLowerCase();
      const q     = search.toLowerCase();
      if (q && !name.includes(q) && !email.includes(q)) return false;
      if (statusFilter !== "all" && r.overall_status !== statusFilter) return false;
      return true;
    }), [reports, search, statusFilter]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-52">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by patient or user…"
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
        </div>
        <div className="flex gap-1.5">
          {[
            { val: "all",                label: "All" },
            { val: "normal",             label: "Normal" },
            { val: "requires_attention", label: "Attention" },
            { val: "critical",           label: "Critical" },
          ].map(({ val, label }) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                statusFilter === val ? "bg-blue-900 text-white border-blue-900" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300"
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500">{filtered.length} of {reports.length} reports</p>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {["Patient", "Uploaded by", "Date", "Status", "Markers", "Abnormal", ""].map(h => (
                <th key={h} className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <tr key={r._id} data-testid={`${ADMIN.reportRow}-${r._id}`} className="hover:bg-slate-50 transition-colors">
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-slate-900">{r.patient_info?.name || "Unknown"}</p>
                  <p className="text-xs text-slate-400 truncate max-w-[160px]">{r.original_filename}</p>
                </td>
                <td className="py-3 px-4">
                  <p className="text-sm text-slate-700">{r.user_name || "—"}</p>
                  <p className="text-xs text-slate-400">{r.user_email}</p>
                </td>
                <td className="py-3 px-4 text-sm text-slate-400 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                <td className="py-3 px-4"><StatusBadge status={r.overall_status} /></td>
                <td className="py-3 px-4 text-sm font-semibold text-slate-900">{r.markers_count}</td>
                <td className="py-3 px-4">
                  {r.abnormal_count > 0
                    ? <span className="text-sm font-semibold text-red-600">{r.abnormal_count}</span>
                    : <span className="text-sm text-slate-300">0</span>}
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <Link to={`/reports/${r._id}`}
                      className="p-1.5 text-slate-400 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors">
                      <ExternalLink size={13} />
                    </Link>
                    <button
                      data-testid={`${ADMIN.deleteReportButton}-${r._id}`}
                      onClick={() => onDelete({ type: "report", id: r._id, label: `${r.patient_info?.name || "Unknown"} — ${r.original_filename}` })}
                      className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center text-slate-400 text-sm py-8">No reports found</p>
        )}
      </div>
    </div>
  );
}

// ── Analytics tab ─────────────────────────────────────────────────────────────

function AnalyticsTab({ analytics }) {
  if (!analytics) return null;
  const { uploads_by_day, top_users } = analytics;

  const total30d = uploads_by_day.reduce((s, d) => s + d.count, 0);
  const avg      = total30d > 0 ? (total30d / 30).toFixed(1) : "0.0";
  const busiest  = uploads_by_day.reduce((a, b) => (b.count > a.count ? b : a), { count: 0, date: "" });
  const maxCount = top_users[0]?.count || 1;

  const fmtAxis = (iso) => {
    try { const d = new Date(iso); return `${d.toLocaleString("en-US",{month:"short"})} ${d.getDate()}`; }
    catch { return iso; }
  };

  return (
    <div data-testid={ADMIN.analyticsTab} className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{total30d}</p>
          <p className="text-xs text-slate-400 mt-1">Uploads — last 30 days</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{avg}</p>
          <p className="text-xs text-slate-400 mt-1">Average uploads / day</p>
        </div>
        <div className={`border rounded-lg p-5 ${busiest.count > 0 ? "bg-blue-50 border-blue-200" : "bg-white border-slate-200"}`}>
          <p className={`text-3xl font-bold ${busiest.count > 0 ? "text-blue-900" : "text-slate-900"}`}
             style={{ fontFamily: "Outfit, sans-serif" }}>{busiest.count}</p>
          <p className="text-xs text-slate-400 mt-1">
            Busiest day{busiest.count > 0 ? ` · ${fmtAxis(busiest.date)}` : ""}
          </p>
        </div>
      </div>

      {/* Upload activity chart */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>
          Upload Activity — Last 30 Days
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={uploads_by_day} margin={{ top: 5, right: 8, bottom: 0, left: -10 }}>
            <defs>
              <linearGradient id="uploadGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#1E3A8A" stopOpacity={0.18} />
                <stop offset="95%" stopColor="#1E3A8A" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: "#94A3B8" }}
              tickLine={false} axisLine={false}
              tickFormatter={(v, i) => i % 5 === 0 ? fmtAxis(v) : ""}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#94A3B8" }}
              tickLine={false} axisLine={false}
              allowDecimals={false} width={22}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const v = payload[0].value;
                return (
                  <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3 py-2 text-xs">
                    <p className="text-slate-500 mb-0.5">{fmtAxis(label)}</p>
                    <p className="font-bold text-slate-900">{v} upload{v !== 1 ? "s" : ""}</p>
                  </div>
                );
              }}
            />
            <Area
              type="monotone" dataKey="count"
              stroke="#1E3A8A" fill="url(#uploadGrad)"
              strokeWidth={2} dot={false}
              activeDot={{ r: 4, fill: "#1E3A8A", stroke: "white", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Top users */}
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-slate-900 mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>
          Most Active Users
        </h3>
        {top_users.length === 0 ? (
          <p className="text-slate-400 text-sm py-4 text-center">No report uploads yet.</p>
        ) : (
          <div className="space-y-4">
            {top_users.map((u, i) => (
              <div key={u.user_id} data-testid={`${ADMIN.topUserRow}-${i}`}
                className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-400 w-4 text-right flex-shrink-0">{i + 1}</span>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold ${
                  i === 0 ? "bg-blue-900" : i === 1 ? "bg-blue-700" : "bg-slate-400"
                }`}>
                  {initials(u.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{u.name}</p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="w-28 bg-slate-100 rounded-full h-1.5">
                    <div
                      className="bg-blue-900 h-1.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.round((u.count / maxCount) * 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-bold text-slate-900 w-6 text-right">{u.count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { user: currentUser } = useAuth();
  const [tab,         setTab]         = useState("overview");
  const [stats,       setStats]       = useState(null);
  const [users,       setUsers]       = useState([]);
  const [reports,     setReports]     = useState([]);
  const [analytics,   setAnalytics]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [pendingDel,  setPendingDel]  = useState(null);
  const [deleting,    setDeleting]    = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, u, r, a] = await Promise.all([
        axios.get(`${API}/admin/stats`,     { withCredentials: true }),
        axios.get(`${API}/admin/users`,     { withCredentials: true }),
        axios.get(`${API}/admin/reports`,   { withCredentials: true }),
        axios.get(`${API}/admin/analytics`, { withCredentials: true }),
      ]);
      setStats(s.data); setUsers(u.data); setReports(r.data); setAnalytics(a.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = async () => {
    if (!pendingDel) return;
    setDeleting(true);
    try {
      if (pendingDel.type === "user") {
        await axios.delete(`${API}/admin/users/${pendingDel.id}`,   { withCredentials: true });
        setUsers(prev => prev.filter(u => u._id !== pendingDel.id));
        setReports(prev => prev.filter(r => r.user_id !== pendingDel.id));
      } else {
        await axios.delete(`${API}/admin/reports/${pendingDel.id}`, { withCredentials: true });
        setReports(prev => prev.filter(r => r._id !== pendingDel.id));
      }
      // Refresh stats
      const s = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(s.data);
      setPendingDel(null);
    } catch (e) { console.error(e); }
    finally { setDeleting(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, role: newRole } : u));
    } catch (e) {
      console.error("Role change failed:", e.response?.data?.detail || e.message);
    }
  };

  const TABS = [
    { id: "overview",   label: "Overview",               testId: ADMIN.tabOverview },
    { id: "analytics",  label: "Analytics",              testId: ADMIN.tabAnalytics },
    { id: "users",      label: `Users (${users.length})`,    testId: ADMIN.tabUsers },
    { id: "reports",    label: `Reports (${reports.length})`, testId: ADMIN.tabReports },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid={ADMIN.page}>
      <Navbar />

      <ConfirmDeleteModal
        target={pendingDel} loading={deleting}
        onConfirm={handleDelete} onCancel={() => !deleting && setPendingDel(null)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-blue-900 flex items-center justify-center">
            <Shield size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Admin Panel</h1>
            <p className="text-slate-400 text-sm">Platform overview and management</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 mb-6 gap-1">
          {TABS.map(t => (
            <button key={t.id}
              data-testid={t.testId}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${
                tab === t.id
                  ? "border-blue-900 text-blue-900"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {tab === "overview"  && stats && <OverviewTab stats={stats} reports={reports} />}
            {tab === "analytics" && <AnalyticsTab analytics={analytics} />}
            {tab === "users"     && <UsersTab   users={users}   currentUserId={currentUser?._id || currentUser?.id} onDelete={setPendingDel} onRoleChange={handleRoleChange} />}
            {tab === "reports"   && <ReportsTab reports={reports} onDelete={setPendingDel} />}
          </>
        )}
      </main>
    </div>
  );
}
